import { useMemo, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, EyeVisible } from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  AtsContractTypesEnum,
  AtsDeleteOfferLetterTemplateDocument,
  AtsJobPostingScheduleTypesEnum,
  AtsOfferLetterTemplatesCrontractTypesDocument,
  AtsSettingsOfferLetterTemplatesDocument,
  AtsSettingsOfferLetterTemplatesItemFragment,
  AtsWorkplaceTypesEnum,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { convertDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import app from 'modules/core/store/models/app'

import { CreateOfferLetterTemplateModal } from 'modules/ats/pages/settings/TemplatesPage/sections/OfferLetterTemplates/CreateTemplate'
import { OfferLetterTemplateSuccessPreviewModal } from 'modules/ats/pages/settings/TemplatesPage/sections/OfferLetterTemplates/SuccessPreviewModal'

interface OfferLetterTemplatesTableFilters {
  contractTypes?: AtsContractTypesEnum[]
  workplaceTypes?: AtsWorkplaceTypesEnum[]
  scheduleTypes?: AtsJobPostingScheduleTypesEnum[]
  tags?: string[]
  active?: boolean[]
}

const DEFAULT_FILTERS: OfferLetterTemplatesTableFilters = {}

const toContractTypes = (value: unknown): AtsContractTypesEnum[] | undefined => {
  if (!Array.isArray(value) || value.length === 0) return undefined
  return value.filter((v): v is AtsContractTypesEnum => typeof v === 'string')
}

const toStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value) || value.length === 0) return undefined
  return value.filter((v): v is string => typeof v === 'string')
}

const toBoolean = (value: unknown): boolean | undefined => {
  if (!Array.isArray(value) || value.length === 0) return undefined
  return typeof value[0] === 'boolean' ? value[0] : undefined
}

export const OfferLetterTemplatesTable = () => {
  const client = useApolloClient()
  const { openDialog } = useConfirmationDialog()

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [successPreviewTemplateId, setSuccessPreviewTemplateId] = useState<number | undefined>(
    undefined
  )
  const [savedFilters, setSavedFilters] = useLocalStorage<
    OfferLetterTemplatesTableFilters | undefined
  >(LOCAL_STORAGE_KEYS.atsOfferLetterTemplatesTableFilters)

  const [deleteAtsOfferLetterTemplate] = useMutation(AtsDeleteOfferLetterTemplateDocument, {
    extractorFn: (data) => data.atsMutations.deleteAtsOfferLetterTemplate,
    refetchQueries: [AtsSettingsOfferLetterTemplatesDocument],
  })

  const removeOfferLetterTemplate = async (id: number) => {
    const result = await deleteAtsOfferLetterTemplate({
      variables: { id },
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.removed_failed'),
      })
    }
  }

  const source = useGraphqlDataCollectionSource({
    document: AtsSettingsOfferLetterTemplatesDocument,
    mapData: (data) =>
      data.ats.offerLetterTemplatesConnection.nodes.map((node) => ({
        ...node,
        team_id: node.team?.id ?? null,
        legal_entity_id: node.legalEntity?.id ?? null,
      })),
    mapPagination: (data) => ({
      total: data.ats.offerLetterTemplatesConnection.totalCount ?? 0,
    }),
    currentFilters: savedFilters ?? DEFAULT_FILTERS,
    mapVariables: ({ search, filters, sortings }) => {
      const contractTypes = toContractTypes(filters.contractTypes)
      const tags = toStringArray(filters.tags)
      const active = toBoolean(filters.active)

      setSavedFilters({
        contractTypes,
        tags,
        active: active ? [active] : undefined,
      })

      return {
        search,
        contractTypes,
        tags,
        active,
        sort: sortings?.[0],
      }
    },
    perPage: 20,
    search: { enabled: true },
    filters: {
      contractTypes: {
        label: i18n.t('ats.settings.templates.offer_letter_templates.filters.contract_types'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: AtsOfferLetterTemplatesCrontractTypesDocument,
              fetchPolicy: 'cache-first',
            })

            return (
              result.data?.ats.contractTypesConnection.nodes.map((contractType) => ({
                label: contractType.label || '',
                value: contractType.id,
              })) || []
            )
          },
        },
      },
      tags: {
        label: i18n.t('ats.settings.templates.offer_letter_templates.filters.tags'),
        type: 'in',
        options: {
          options: [],
        },
      },
      active: {
        label: i18n.t('ats.settings.templates.offer_letter_templates.filters.active'),
        type: 'in',
        options: {
          options: [
            {
              label: i18n.t('ats.settings.templates.offer_letter_templates.filters.active_active'),
              value: true,
            },
            {
              label: i18n.t(
                'ats.settings.templates.offer_letter_templates.filters.active_inactive'
              ),
              value: false,
            },
          ],
        },
      },
    },
    presets: [
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.filters.active_active'),
        filter: { active: [true] },
      },
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.filters.active_inactive'),
        filter: { active: [false] },
      },
    ],
    primaryActions: () => {
      return [
        {
          label: i18n.t('ats.settings.templates.offer_letter_templates.create_template'),
          icon: Add,
          onClick: () => setShowCreateModal(true),
        },
      ]
    },
    itemActions: (offerLetterTemplate) => [
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.view_template'),
        icon: EyeVisible,
        onClick: () => setSuccessPreviewTemplateId(offerLetterTemplate.id),
      },
      {
        label: i18n.t('common.delete'),
        icon: Delete,
        critical: true,
        onClick: () => {
          openDialog({
            title: i18n.t('ats.settings.templates.offer_letter_templates.delete_modal.title'),
            description: i18n.t(
              'ats.settings.templates.offer_letter_templates.delete_modal.description'
            ),
            cancelLabel: i18n.t('common.cancel'),
            confirmLabel: i18n.t('common.delete'),
            onConfirm: () => removeOfferLetterTemplate(offerLetterTemplate.id),
          })
        },
      },
    ],
  })

  const tableColumns = useMemo(
    () => [
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.name'),
        sorting: 'name',
        render: (template: AtsSettingsOfferLetterTemplatesItemFragment) => ({
          type: 'text' as const,
          value: template.name,
        }),
      },
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.legal_entity'),
        sorting: 'legal_entity_id',
        render: (template: AtsSettingsOfferLetterTemplatesItemFragment) => ({
          type: 'text' as const,
          value: template.legalEntity?.legalName || '',
        }),
      },
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.tags'),
        render: ({ tags }: AtsSettingsOfferLetterTemplatesItemFragment) => {
          const items = tags?.filter((tag) => tag !== null && tag !== undefined) || []
          if (items.length > 0) {
            return {
              type: 'tagList' as const,
              value: {
                type: 'raw' as const,
                tags: items.map((tag) => ({
                  text: tag,
                })),
              },
            }
          }
        },
      },
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.detected_variables'),
        render: ({
          systemVariables,
          customVariables,
        }: AtsSettingsOfferLetterTemplatesItemFragment) => {
          const systemCount = systemVariables?.length ?? 0
          const customCount = customVariables?.length ?? 0
          const totalCount = systemCount + customCount

          return {
            type: 'alertTag' as const,
            value: {
              label: String(totalCount),
              level: totalCount === 0 ? ('warning' as const) : ('info' as const),
            },
          }
        },
      },
      {
        label: i18n.t('ats.settings.templates.offer_letter_templates.columns.created_at'),
        render: ({ createdAt }: AtsSettingsOfferLetterTemplatesItemFragment) =>
          convertDate(createdAt, 'backend_datetime', 'ui_abbreviated_month_year'),
      },
    ],
    []
  )

  const visualizations = useMemo(
    () => [
      {
        type: 'table' as const,
        options: {
          columns: tableColumns,
        },
      },
    ],
    [tableColumns]
  )

  return (
    <>
      <OneDataCollection source={source} visualizations={visualizations} />
      {showCreateModal && (
        <CreateOfferLetterTemplateModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={(id) => {
            setShowCreateModal(false)
            setSuccessPreviewTemplateId(id)
          }}
        />
      )}

      {!!successPreviewTemplateId && (
        <OfferLetterTemplateSuccessPreviewModal
          onClose={() => setSuccessPreviewTemplateId(undefined)}
          id={successPreviewTemplateId}
        />
      )}
    </>
  )
}
